export interface EventVenue {
  name: string
  address: string
  capacity: number
  mapUrl: string
}

export interface EventTicket {
  type: string
  price: number
  available: boolean
  benefits?: string[]
}

export interface Event {
  id: string
  slug: string
  title: string
  description: string
  fullDescription: string
  category:
    | 'Music'
    | 'Arts & Culture'
    | 'Sports'
    | 'Food & Drink'
    | 'Business'
    | 'Family'
  date: string
  time: string
  endTime?: string
  venue: EventVenue
  image: string
  organizer: string
  tickets: EventTicket[]
  highlights: string[]
  ageRestriction?: string
  dresscode?: string
  featured: boolean
  tags: string[]
}

export const events: Event[] = [
  {
    id: '1',
    slug: 'asake-gabzy-live-nairobi',
    title: 'Asake & Gabzy Live in Nairobi',
    description:
      "Nigerian Afrobeats sensation Asake teams up with rising star Gabzy for Nairobi's biggest concert of the year.",
    fullDescription: `
      <p class="text-xl text-gray-700 leading-relaxed mb-6">
        Get ready for an unforgettable night as Nigerian Afrobeats superstar <strong>Asake</strong> joins forces with the electrifying <strong>Gabzy</strong> for what promises to be Nairobi's most anticipated concert. The Kasarani Stadium will transform into a massive celebration of African music, culture, and unity.
      </p>

      <h2 class="text-3xl font-display font-bold text-charcoal mb-4 mt-8">About the Artists</h2>
      
      <p class="text-lg text-gray-700 leading-relaxed mb-6">
        <strong>Asake</strong>, whose real name is Ahmed Ololade, has taken the African music scene by storm with his unique blend of Afrobeats, Amapiano, and Fuji. With chart-topping hits like "Sungba," "Terminator," and "Lonely at the Top," the YBNL signee has amassed a devoted following across the continent.
      </p>

      <p class="text-lg text-gray-700 leading-relaxed mb-6">
        <strong>Gabzy</strong>, the rising star known for his smooth vocals and captivating stage presence, brings his own flavor to the mix. His recent collaborations and solo projects have positioned him as one of the most exciting new voices in Afrobeats.
      </p>

      <h2 class="text-3xl font-display font-bold text-charcoal mb-4 mt-8">What to Expect</h2>

      <p class="text-lg text-gray-700 leading-relaxed mb-6">
        This isn't just a concert—it's an experience. Expect world-class production, stunning visual effects, and surprise guest appearances from some of East Africa's finest artists.
      </p>
    `,
    category: 'Music',
    date: '2024-12-20',
    time: '19:00',
    endTime: '23:00',
    venue: {
      name: 'Kasarani Stadium',
      address: 'Thika Road, Kasarani, Nairobi',
      capacity: 60000,
      mapUrl: 'https://maps.google.com/?q=Kasarani+Stadium+Nairobi',
    },
    image:
      'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1200&q=80',
    organizer: 'Live Nation Africa',
    tickets: [
      {
        type: 'Regular',
        price: 2500,
        available: false,
        benefits: [
          'General admission',
          'Access to food vendors',
          'Standing area',
        ],
      },
      {
        type: 'VIP',
        price: 5000,
        available: false,
        benefits: [
          'Reserved seating',
          'VIP lounge access',
          'Complimentary drink',
        ],
      },
      {
        type: 'VVIP',
        price: 10000,
        available: false,
        benefits: [
          'Front row seating',
          'Exclusive VVIP lounge',
          'Meet & greet opportunity',
        ],
      },
    ],
    highlights: [
      'Asake performing chart-topping hits',
      'Gabzy live performance',
      'Surprise guest artists',
      'World-class production',
    ],
    ageRestriction: '18+',
    dresscode: 'Smart casual',
    featured: false,
    tags: ['Afrobeats', 'Concert', 'Live Music', 'Nigerian Artists'],
  },
  {
    id: '2',
    slug: 'nairobi-food-festival-2025',
    title: 'Nairobi Food Festival 2025',
    description:
      "A celebration of Kenya's diverse culinary heritage featuring top chefs, street food vendors, and cooking demonstrations.",
    fullDescription: `
      <p class="text-xl text-gray-700 leading-relaxed mb-6">
        The <strong>Nairobi Food Festival 2025</strong> returns for its 6th edition, bringing together the best of Kenya's culinary scene in one spectacular weekend.
      </p>
    `,
    category: 'Food & Drink',
    date: '2025-03-15',
    time: '10:00',
    endTime: '20:00',
    venue: {
      name: 'Uhuru Gardens',
      address: 'Langata Road, Nairobi',
      capacity: 15000,
      mapUrl: 'https://maps.google.com/?q=Uhuru+Gardens+Nairobi',
    },
    image:
      'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=1200&q=80',
    organizer: 'Nairobi Food Collective',
    tickets: [
      {
        type: 'Day Pass',
        price: 500,
        available: true,
        benefits: ['Single day entry', 'Access to all food stalls'],
      },
      {
        type: 'Weekend Pass',
        price: 800,
        available: true,
        benefits: [
          'Both days entry',
          'Access to all areas',
          '10% discount at vendors',
        ],
      },
      {
        type: 'VIP Experience',
        price: 2500,
        available: true,
        benefits: ['Weekend access', 'VIP lounge', 'Complimentary tastings'],
      },
    ],
    highlights: [
      '100+ food vendors',
      'Celebrity chef demonstrations',
      'Wine & spirits tastings',
      'Food competitions',
    ],
    ageRestriction: 'All ages welcome',
    dresscode: 'Casual',
    featured: true,
    tags: ['Food', 'Family-Friendly', 'Weekend Event'],
  },
  {
    id: '3',
    slug: 'nairobi-tech-summit-2025',
    title: 'Nairobi Tech Summit 2025',
    description:
      "East Africa's premier technology conference bringing together innovators, investors, and entrepreneurs.",
    fullDescription: `
      <p class="text-xl text-gray-700 leading-relaxed mb-6">
        The <strong>Nairobi Tech Summit 2025</strong> is East Africa's flagship technology conference.
      </p>
    `,
    category: 'Business',
    date: '2025-04-22',
    time: '08:00',
    endTime: '18:00',
    venue: {
      name: 'Kenyatta International Convention Centre',
      address: 'Harambee Avenue, Nairobi CBD',
      capacity: 5000,
      mapUrl: 'https://maps.google.com/?q=KICC+Nairobi',
    },
    image:
      'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
    organizer: 'Tech Events Africa',
    tickets: [
      {
        type: 'Student',
        price: 3000,
        available: true,
        benefits: ['3-day access', 'All sessions', 'Exhibition hall'],
      },
      {
        type: 'Professional',
        price: 15000,
        available: true,
        benefits: ['3-day access', 'All sessions', 'Networking events'],
      },
      {
        type: 'VIP Pass',
        price: 35000,
        available: true,
        benefits: ['3-day VIP access', 'Front row seating', 'VIP lounge'],
      },
    ],
    highlights: [
      '50+ expert speakers',
      'Startup pitch competition',
      '100+ exhibitors',
      'Networking sessions',
    ],
    ageRestriction: 'Professional event',
    dresscode: 'Business casual',
    featured: false,
    tags: ['Technology', 'Business', 'Networking', 'Startups'],
  },
  {
    id: '4',
    slug: 'nairobi-marathon-2025',
    title: 'Nairobi City Marathon 2025',
    description:
      "Join thousands of runners in Kenya's premier marathon event through the city.",
    fullDescription: `
      <p class="text-xl text-gray-700 leading-relaxed mb-6">
        The <strong>Nairobi City Marathon 2025</strong> is back for its annual celebration of running and fitness.
      </p>
    `,
    category: 'Sports',
    date: '2025-05-18',
    time: '06:00',
    endTime: '12:00',
    venue: {
      name: 'Uhuru Park',
      address: 'Uhuru Highway, Nairobi',
      capacity: 20000,
      mapUrl: 'https://maps.google.com/?q=Uhuru+Park+Nairobi',
    },
    image:
      'https://images.unsplash.com/photo-1452626038306-9aae5e071dd3?w=1200&q=80',
    organizer: 'Kenya Athletics Federation',
    tickets: [
      {
        type: '5K Fun Run',
        price: 1000,
        available: true,
        benefits: ['Race bib & timing chip', 'Finisher medal', 'Race t-shirt'],
      },
      {
        type: '10K Race',
        price: 1500,
        available: true,
        benefits: [
          'Race bib & timing chip',
          'Finisher medal',
          'Water stations',
        ],
      },
      {
        type: 'Half Marathon',
        price: 2500,
        available: true,
        benefits: [
          'Race bib & timing chip',
          'Premium race t-shirt',
          'Medical support',
        ],
      },
      {
        type: 'Full Marathon',
        price: 3500,
        available: true,
        benefits: [
          'Race bib & timing chip',
          'Premium race t-shirt',
          'Professional race photos',
        ],
      },
    ],
    highlights: [
      'Scenic route through Nairobi',
      'Professional timing & results',
      'Medical support throughout',
      'Post-race celebration',
    ],
    ageRestriction: '18+ for full/half marathon, all ages for 10K/5K',
    dresscode: 'Athletic wear',
    featured: true,
    tags: ['Sports', 'Running', 'Fitness', 'Community'],
  },
]

export function getEventBySlug(slug: string): Event | undefined {
  return events.find((event) => event.slug === slug)
}

export function isEventPast(eventDate: string): boolean {
  const now = new Date()
  const event = new Date(eventDate)
  return event < now
}

export function getUpcomingEvents(): Event[] {
  const now = new Date()
  return events
    .filter((event) => new Date(event.date) >= now)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
}

export function getPastEvents(): Event[] {
  const now = new Date()
  return events
    .filter((event) => new Date(event.date) < now)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

export function getFeaturedEvents(): Event[] {
  return events.filter((event) => event.featured && !isEventPast(event.date))
}

export const eventCategories = [
  'All',
  'Music',
  'Arts & Culture',
  'Sports',
  'Food & Drink',
  'Business',
  'Family',
] as const
